/**
 * Piglin bartering (Bedrock 1.21 `gameplay/piglin_barter`): the gold ingot
 * trade table, the gold-armour / anger hooks main.ts wires up, and the shared
 * "who is angry about what" helpers for chests and gold mining.
 *
 * Table entries whose item the game does not have yet (spectral arrow,
 * soul-speed boots / book) are kept at their vanilla weight with
 * `item: null`; a roll that lands on one is re-rolled, so the remaining
 * entries are weighted relative to each other exactly as vanilla would.
 * (Hook: fill in the null items when spectral arrows / soul speed land.)
 */

import { BLACKSTONE_ID, BLOCKS, CRYING_OBSIDIAN_ID, GRAVEL_ID, OBSIDIAN_ID, SOUL_SAND_ID } from '../../blocks';
import { I } from '../../game/itemIds';
import { realm } from '../realm';
import { potionStack } from '../../game/brewing/potions';
import { stackOf, type Stack } from '../../game/types';

export interface BarterEntry {
  /** Item or block id; null = not implemented in this game yet (re-rolled). */
  item: number | null;
  weight: number;
  min: number;
  max: number;
  /** Vanilla name, for tests / debugging. */
  name: string;
  /** Builds a stack that is not a plain id (potions); `item` is then the container item id. */
  make?: () => Stack;
}

export const BARTER_TABLE: readonly BarterEntry[] = [
  { name: 'soul_speed_book', item: null, weight: 5, min: 1, max: 1 },
  { name: 'iron_boots_soul_speed', item: null, weight: 8, min: 1, max: 1 },
  { name: 'splash_fire_resistance', item: I.SPLASH_POTION, weight: 8, min: 1, max: 1, make: () => potionStack('fire_resistance', 'splash') },
  { name: 'fire_resistance', item: I.POTION, weight: 8, min: 1, max: 1, make: () => potionStack('fire_resistance') },
  { name: 'water_bottle', item: I.POTION, weight: 10, min: 1, max: 1, make: () => potionStack('water') },
  { name: 'iron_nugget', item: I.IRON_NUGGET, weight: 10, min: 9, max: 36 },
  { name: 'ender_pearl', item: I.ENDER_PEARL, weight: 10, min: 2, max: 4 },
  { name: 'string', item: I.STRING, weight: 20, min: 3, max: 9 },
  { name: 'quartz', item: I.QUARTZ, weight: 20, min: 5, max: 12 },
  { name: 'obsidian', item: OBSIDIAN_ID, weight: 40, min: 1, max: 1 },
  { name: 'crying_obsidian', item: CRYING_OBSIDIAN_ID, weight: 40, min: 1, max: 3 },
  { name: 'fire_charge', item: I.FIRE_CHARGE, weight: 40, min: 1, max: 1 },
  { name: 'leather', item: I.LEATHER, weight: 40, min: 2, max: 4 },
  { name: 'soul_sand', item: SOUL_SAND_ID, weight: 40, min: 2, max: 8 },
  { name: 'nether_brick', item: I.NETHER_BRICK, weight: 40, min: 2, max: 8 },
  { name: 'spectral_arrow', item: null, weight: 40, min: 6, max: 12 },
  { name: 'gravel', item: GRAVEL_ID, weight: 40, min: 8, max: 16 },
  { name: 'blackstone', item: BLACKSTONE_ID, weight: 40, min: 8, max: 16 },
];

export const BARTER_TOTAL_WEIGHT = BARTER_TABLE.reduce((n, e) => n + e.weight, 0);

/** Pick a table entry by weight (all entries, including unimplemented ones). */
export function pickBarterEntry(rnd: () => number): BarterEntry {
  let r = rnd() * BARTER_TOTAL_WEIGHT;
  for (const e of BARTER_TABLE) { if ((r -= e.weight) < 0) return e; }
  return BARTER_TABLE[BARTER_TABLE.length - 1];
}

/** One barter result; unimplemented entries are re-rolled. */
export function rollBarter(rnd: () => number): Stack {
  for (let i = 0; i < 64; i++) {
    const e = pickBarterEntry(rnd);
    if (e.item === null) continue;
    if (e.make) return e.make();
    return stackOf(e.item, e.min + Math.floor(rnd() * (e.max - e.min + 1)));
  }
  return stackOf(I.IRON_NUGGET, 9);
}

/** Ticks a piglin admires the ingot before throwing the result (6 s). */
export const ADMIRE_TICKS = 120;
/** Range a chest / gold-mining anger spreads over. */
export const PIGLIN_ANGER_RANGE = 16;

/** The player a piglin is weighing up: '' / absent id = the local one, else a remote peer with its replicated armour ids. */
export interface PiglinTarget { readonly id?: string; readonly armor?: readonly number[] }

/** Wiring main.ts sets: does that player wear any gold armour piece? */
export const piglinHooks: {
  wearsGold: (target?: PiglinTarget) => boolean;
  /** Can this piglin see the local player? Chest / gold anger only reaches piglins that watched it happen (vanilla PiglinAi.angerNearbyPiglins). */
  canSeePlayer?: (p: PiglinLike) => boolean;
  /** Nearest dropped gold ingot within `range` blocks; `take()` removes one from the world. main.ts wires this to the item entities. */
  findGold?: (x: number, y: number, z: number, range: number) => { x: number; y: number; z: number; take(): void } | null;
} = { wearsGold: () => false };

/** Anything that can be angered by block events (Piglin registers itself). */
export interface PiglinLike {
  x: number; y: number; z: number; removed: boolean; dying: boolean;
  /** The world it lives in (entities/realm.ts). */
  readonly realm?: number;
  provoke(ticks: number): void;
}
export const piglinRegistry = new Set<PiglinLike>();

/**
 * Nearby piglins turn hostile: a piglin was hit (the group joins in), or - with
 * `witnessed` - the player opened a chest / broke a guarded block, which only
 * angers the piglins that can see the player do it.
 */
export function angerPiglinsNear(x: number, y: number, z: number, range = PIGLIN_ANGER_RANGE, witnessed = false): number {
  let n = 0;
  for (const p of piglinRegistry) {
    if (p.removed) { piglinRegistry.delete(p); continue; }
    if (p.dying || (p.realm !== undefined && p.realm !== realm.id)) continue;
    const dx = p.x - x, dy = p.y - y, dz = p.z - z;
    if (dx * dx + dy * dy + dz * dz > range * range) continue;
    if (witnessed && piglinHooks.canSeePlayer && !piglinHooks.canSeePlayer(p)) continue;
    p.provoke(600);
    n++;
  }
  return n;
}

/** Vanilla `guarded_by_piglins`, as far as this game has the blocks (every facing / open state of a chest shares its name). */
const GUARDED_NAMES = new Set([
  'Gold Ore', 'Deepslate Gold Ore', 'Nether Gold Ore', 'Block of Gold', 'Block of Raw Gold', 'Gilded Blackstone',
  'Chest', 'Trapped Chest', 'Ender Chest', 'Barrel', 'Shulker Box',
]);
/** Blocks whose breaking angers the piglins watching. */
export const PIGLIN_ANGERING_BLOCKS: ReadonlySet<number> = new Set(
  BLOCKS.flatMap((b, id) => (b && GUARDED_NAMES.has(b.name) ? [id] : [])),
);
